/**
 * Period boundary helpers — mirrors src/lib/dates.ts (America/Toronto, Monday weeks).
 * Kept in packages/web so the grid is self-contained in the frontend package.
 */
import {
  addMonths,
  differenceInCalendarDays,
  format,
  max,
  min,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  subDays,
  addDays,
  endOfMonth,
  endOfQuarter,
} from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";

import type { PeriodId } from "@/grid/core/types";

export const WAREHOUSE_TIMEZONE = "America/Toronto";

export function nowToronto(): Date {
  return toZonedTime(new Date(), WAREHOUSE_TIMEZONE);
}

export function toIsoDate(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

/** Parse YYYY-MM-DD as a Toronto calendar day (not UTC midnight). */
export function parseTorontoIsoDate(isoDate: string): Date {
  return fromZonedTime(`${isoDate}T12:00:00`, WAREHOUSE_TIMEZONE);
}

export function weekStartMonday(d: Date = nowToronto()): string {
  return toIsoDate(startOfWeek(d, { weekStartsOn: 1 }));
}

export function priorWeekMonday(d: Date = nowToronto()): string {
  return toIsoDate(subDays(startOfWeek(d, { weekStartsOn: 1 }), 7));
}

export function monthStart(d: Date = nowToronto()): string {
  return toIsoDate(startOfMonth(d));
}

/** Calendar month (ISO start) containing a Monday week start. */
export function monthStartForWeekMonday(weekMondayIso: string): string {
  return monthStart(parseISO(weekMondayIso));
}

/** Monday of the week containing the first day of the month (bb-grid MTD boundary). */
export function monthBoundaryWeekStart(d: Date = nowToronto()): string {
  return toIsoDate(startOfWeek(startOfMonth(d), { weekStartsOn: 1 }));
}

export function quarterStart(d: Date = nowToronto()): string {
  return toIsoDate(startOfQuarter(d));
}

/**
 * Earliest daily-activity date the grid needs. Covers QTD daily sums AND last
 * week's dailies — in the first week of a quarter, prior-week Monday is earlier
 * than quarter start (regression: 2026-07-03 grid showed 0 calls/meetings for
 * "Last" week because the fetch started at Jul 1).
 */
export function dailyFetchFrom(asOf: Date = nowToronto()): string {
  const candidates = [priorWeekMonday(asOf), quarterStart(asOf)];
  return candidates.sort()[0]!;
}

export interface PeriodKeys {
  /** Inclusive ISO dates for daily-sum metrics. */
  dailyFrom?: string;
  dailyTo?: string;
  /** Stored period row lookup. */
  periodType?: "week" | "month";
  periodStart?: string;
  /** Sum monthly rows from this date (QTD). */
  quarterFrom?: string;
}

/** Map a period id to the keys needed to resolve metric values. */
export function resolvePeriodKeys(periodId: PeriodId, asOf: Date = nowToronto()): PeriodKeys {
  const asOfIso = toIsoDate(asOf);
  const thisMonday = weekStartMonday(asOf);

  switch (periodId) {
    case "wtd":
      return {
        dailyFrom: thisMonday,
        dailyTo: asOfIso,
        periodType: "week",
        periodStart: thisMonday,
      };
    case "lastWeek": {
      const lastMonday = priorWeekMonday(asOf);
      return {
        dailyFrom: lastMonday,
        dailyTo: toIsoDate(subDays(startOfWeek(asOf, { weekStartsOn: 1 }), 1)),
        periodType: "week",
        periodStart: lastMonday,
      };
    }
    case "mtd":
      return {
        dailyFrom: monthStart(asOf),
        dailyTo: asOfIso,
        periodType: "month",
        periodStart: monthStart(asOf),
      };
    case "qtd":
      return {
        dailyFrom: quarterStart(asOf),
        dailyTo: asOfIso,
        quarterFrom: quarterStart(asOf),
        periodType: "month",
        periodStart: monthStart(asOf),
      };
  }

  return {};
}

/** ISO month starts from quarter start through the month containing asOf (for QTD actuals). */
export function monthStartsInQuarter(asOf: Date = nowToronto()): string[] {
  const end = startOfMonth(asOf);
  const result: string[] = [];
  let cursor = startOfMonth(startOfQuarter(asOf));

  while (cursor <= end) {
    result.push(toIsoDate(cursor));
    cursor = startOfMonth(addMonths(cursor, 1));
  }

  return result;
}

/** All ISO month starts in the calendar quarter (for QTD target roll-up from monthly budgets). */
export function allMonthStartsInCalendarQuarter(asOf: Date = nowToronto()): string[] {
  const result: string[] = [];
  let cursor = startOfMonth(startOfQuarter(asOf));
  const quarterEnd = startOfMonth(endOfQuarter(asOf));

  while (cursor <= quarterEnd) {
    result.push(toIsoDate(cursor));
    cursor = startOfMonth(addMonths(cursor, 1));
  }

  return result;
}

/** All ISO month starts in the calendar year containing asOf. */
export function allMonthStartsInCalendarYear(asOf: Date = nowToronto()): string[] {
  const year = asOf.getFullYear();
  return Array.from({ length: 12 }, (_, month) => toIsoDate(new Date(year, month, 1)));
}

/** ISO month starts from Jan 1 through the month containing asOf (YTD actuals). */
export function monthStartsInYearThrough(asOf: Date = nowToronto()): string[] {
  const end = startOfMonth(asOf);
  const result: string[] = [];
  let cursor = startOfMonth(new Date(asOf.getFullYear(), 0, 1));

  while (cursor <= end) {
    result.push(toIsoDate(cursor));
    cursor = startOfMonth(addMonths(cursor, 1));
  }

  return result;
}

/** ISO quarter starts (Jan, Apr, Jul, Oct) in the calendar year containing asOf. */
export function quarterStartsInCalendarYear(asOf: Date = nowToronto()): string[] {
  const year = asOf.getFullYear();
  return [0, 3, 6, 9].map((month) => toIsoDate(new Date(year, month, 1)));
}

function weeksOverlappingRange(rangeStart: Date, rangeEnd: Date): string[] {
  let cursor = startOfWeek(rangeStart, { weekStartsOn: 1 });
  const result: string[] = [];

  while (cursor <= rangeEnd) {
    const weekEnd = addDays(cursor, 6);
    if (weekEnd >= rangeStart && cursor <= rangeEnd) {
      result.push(toIsoDate(cursor));
    }
    cursor = addDays(cursor, 7);
  }

  return result;
}

/** Monday week starts that overlap the calendar month containing asOf. */
export function weekStartsOverlappingMonth(asOf: Date = nowToronto()): string[] {
  return weeksOverlappingRange(startOfMonth(asOf), endOfMonth(asOf));
}

/** Monday week starts that overlap the calendar quarter containing asOf. */
export function weekStartsOverlappingQuarter(asOf: Date = nowToronto()): string[] {
  return weeksOverlappingRange(startOfQuarter(asOf), endOfQuarter(asOf));
}

/** Weeks overlapping the month, limited to weeks that have started by asOf. */
export function weekStartsOverlappingMonthThrough(asOf: Date = nowToronto()): string[] {
  const thisMonday = weekStartMonday(asOf);
  return weekStartsOverlappingMonth(asOf).filter((monday) => monday <= thisMonday);
}

/** Weeks overlapping the quarter, limited to weeks that have started by asOf. */
export function weekStartsOverlappingQuarterThrough(asOf: Date = nowToronto()): string[] {
  const thisMonday = weekStartMonday(asOf);
  return weekStartsOverlappingQuarter(asOf).filter((monday) => monday <= thisMonday);
}

function countDaysInClip(rangeStart: Date, rangeEnd: Date, clipStart: Date, clipEnd: Date): number {
  const start = max([rangeStart, clipStart]);
  const end = min([rangeEnd, clipEnd]);
  if (start > end) return 0;
  return differenceInCalendarDays(end, start) + 1;
}

/** Calendar days of a Mon–Sun week that fall inside the month containing asOf. */
export function weekDaysInMonth(weekMondayIso: string, monthAnchor: Date, through?: Date): number {
  const weekStart = parseISO(weekMondayIso);
  const weekEnd = addDays(weekStart, 6);
  const monthStartDate = startOfMonth(monthAnchor);
  const monthEndDate = endOfMonth(monthAnchor);
  const clipEnd = through ? min([monthEndDate, through]) : monthEndDate;
  return countDaysInClip(weekStart, weekEnd, monthStartDate, clipEnd);
}

/** Calendar days of a Mon–Sun week that fall inside the quarter containing asOf. */
export function weekDaysInQuarter(
  weekMondayIso: string,
  quarterAnchor: Date,
  through?: Date,
): number {
  const weekStart = parseISO(weekMondayIso);
  const weekEnd = addDays(weekStart, 6);
  const quarterStartDate = startOfQuarter(quarterAnchor);
  const quarterEndDate = endOfQuarter(quarterAnchor);
  const clipEnd = through ? min([quarterEndDate, through]) : quarterEndDate;
  return countDaysInClip(weekStart, weekEnd, quarterStartDate, clipEnd);
}

/** Share of a Mon–Sun week (0–1) that belongs to the month containing asOf. */
export function weekFractionInMonth(weekMondayIso: string, monthAnchor: Date): number {
  return weekDaysInMonth(weekMondayIso, monthAnchor) / 7;
}

/** Share of a Mon–Sun week (0–1) in the month, counting only days through asOf. */
export function weekFractionInMonthThrough(weekMondayIso: string, asOf: Date): number {
  return weekDaysInMonth(weekMondayIso, asOf, asOf) / 7;
}

/** Share of a Mon–Sun week (0–1) that belongs to the quarter containing asOf. */
export function weekFractionInQuarter(weekMondayIso: string, quarterAnchor: Date): number {
  return weekDaysInQuarter(weekMondayIso, quarterAnchor) / 7;
}

/** Share of a Mon–Sun week (0–1) in the quarter, counting only days through asOf. */
export function weekFractionInQuarterThrough(weekMondayIso: string, asOf: Date): number {
  return weekDaysInQuarter(weekMondayIso, asOf, asOf) / 7;
}

/** Sum of week fractions for all Mon–Sun weeks overlapping the month (always ≈ daysInMonth / 7). */
export function sumWeekFractionsInMonth(monthAnchor: Date): number {
  return weekStartsOverlappingMonth(monthAnchor).reduce(
    (total, monday) => total + weekFractionInMonth(monday, monthAnchor),
    0,
  );
}

/** Sum of week fractions for all Mon–Sun weeks overlapping the quarter. */
export function sumWeekFractionsInQuarter(quarterAnchor: Date): number {
  return weekStartsOverlappingQuarter(quarterAnchor).reduce(
    (total, monday) => total + weekFractionInQuarter(monday, quarterAnchor),
    0,
  );
}
